/**
 * One pictogram per station, for paper.
 *
 * There are two drawings of every station and both earn their place.
 * `drawStationFixture` is tile-scale full-colour equipment standing on a lit
 * walnut bench, and it reads by contrast against that bench. Put the same paths
 * on cream ticket paper at 26 pixels and they collapse into a black square —
 * which is why the badge exists at all: open outlines in ticket ink, a couple of
 * marks each. Ink is filled in two places only — a knife blade and a burner
 * flame — where an outline of the mark would close up at 26px anyway.
 *
 * What this file fixes is that the badge set used to stop after four. Only
 * `stove`, `oven`, `sink` and `board` had bespoke paths and everything else fell
 * to a rounded square, so on a ticket a bin, a hatch, a plate return, a plate
 * stack, a crate and a counter were the same picture. A player taught the
 * kitchen by the tutorial then read a route strip that could not tell them where
 * to go.
 *
 * Two rules hold the table together:
 *
 *  - **A badge is a reduction of its fixture, not a new idea.** The bin keeps
 *    its tapered can and lid, the pass keeps its window and sill, the plate
 *    return keeps its chute, the crate keeps its slats and corner posts.
 *    What is dropped is colour, depth, animation and anything that disappears
 *    at 26 pixels — never the silhouette, because the silhouette is what the
 *    player learned from the tile. When a fixture is redrawn its badge is
 *    redrawn with it, which is why the hatch's arch and bell are gone from
 *    both at once.
 *  - **The table is exhaustive by type.** `Record<StationType, BadgePath>` means
 *    adding a station to the contract fails to compile until it has a badge.
 *    The old gap was silent; this one cannot be.
 *
 * Geometry is written in units of `r`, the half-width of the box the badge fills,
 * around `(cx, cy)` — the same convention every other icon here uses, so a
 * caller never has to know how tall a given station is. Nothing strays past
 * `1.0r`, which leaves room for the stroke inside the canvas the caller mounts.
 */

import type { StationType } from '@overcooked/shared'
import { COLOURS, roundRect } from './theme'

const TAU = Math.PI * 2

/** Ticket ink. Everything in this file strokes in it; `RouteIcon` assumes it. */
export const BADGE_INK = '#2a2118'

/**
 * One station's pictogram.
 *
 * `lw` is handed in rather than read back off the context because several paths
 * want a *fraction* of the badge's stroke for their inner marks — a bin's ribs
 * or an oven's window — and reading `ctx.lineWidth` after having changed it is
 * how those drift apart.
 */
export type BadgePath = (
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  r: number,
  lw: number,
) => void

/** A short line, the unit most of these are built from. */
function line(ctx: CanvasRenderingContext2D, x1: number, y1: number, x2: number, y2: number): void {
  ctx.beginPath()
  ctx.moveTo(x1, y1)
  ctx.lineTo(x2, y2)
  ctx.stroke()
}

/** The direction mark. `towards` is +1 for rightward, -1 for leftward. */
function arrow(ctx: CanvasRenderingContext2D, cx: number, y: number, r: number, towards: 1 | -1): void {
  const tip = cx + r * 0.62 * towards
  const tail = cx - r * 0.62 * towards
  ctx.beginPath()
  ctx.moveTo(tail, y)
  ctx.lineTo(tip, y)
  ctx.moveTo(tip - r * 0.22 * towards, y - r * 0.18)
  ctx.lineTo(tip, y)
  ctx.lineTo(tip - r * 0.22 * towards, y + r * 0.18)
  ctx.stroke()
}

/**
 * A flame, static and small.
 *
 * Not `icons.drawFlame`: that one wobbles on a tick and this one has no tick,
 * and importing it would put a cycle between the icon library and its own badge
 * table for eleven lines of curve. It keeps the two-tongue silhouette and the
 * ember colours, because the flame is the one place a badge is allowed colour —
 * it is what tells a hob from an oven at a glance on a strip.
 */
function badgeFlame(ctx: CanvasRenderingContext2D, cx: number, cy: number, r: number): void {
  ctx.save()
  for (const [scale, colour] of [
    [1, COLOURS.ember],
    [0.58, '#ffd24a'],
  ] as Array<[number, string]>) {
    const height = r * scale * 1.5
    ctx.beginPath()
    ctx.moveTo(cx - r * 0.62 * scale, cy)
    ctx.quadraticCurveTo(cx - r * 0.5 * scale, cy - height * 0.55, cx, cy - height)
    ctx.quadraticCurveTo(cx + r * 0.62 * scale, cy - height * 0.5, cx + r * 0.62 * scale, cy)
    ctx.quadraticCurveTo(cx, cy + r * 0.2 * scale, cx - r * 0.62 * scale, cy)
    ctx.closePath()
    ctx.fillStyle = colour
    ctx.fill()
  }
  ctx.restore()
}

/**
 * The pictogram for every station type there is.
 *
 * Exported as a table rather than hidden inside a `switch` so it can be walked:
 * the test enumerates it against `STATION_TYPES` and asserts no two stations
 * draw the same thing, which is the property that actually broke.
 */
export const BADGE_PATHS: Record<StationType, BadgePath> = {
  // Not route steps — no recipe references a wall or a floor — but the table is
  // exhaustive by type, and a debug surface that asks for one should get
  // something honest rather than the fallback square.
  wall: (ctx, cx, cy, r) => {
    // Brickwork: two courses with the joints offset, which is the only thing
    // that says "wall" rather than "box".
    roundRect(ctx, cx - r * 0.86, cy - r * 0.7, r * 1.72, r * 1.4, r * 0.12)
    ctx.stroke()
    line(ctx, cx - r * 0.86, cy, cx + r * 0.86, cy)
    line(ctx, cx, cy - r * 0.7, cx, cy)
    line(ctx, cx - r * 0.43, cy, cx - r * 0.43, cy + r * 0.7)
    line(ctx, cx + r * 0.43, cy, cx + r * 0.43, cy + r * 0.7)
  },
  floor: (ctx, cx, cy, r) => {
    // An empty tile: the square, and the inset that makes it a floor tile and
    // not a crate lid.
    roundRect(ctx, cx - r * 0.78, cy - r * 0.78, r * 1.56, r * 1.56, r * 0.14)
    ctx.stroke()
    roundRect(ctx, cx - r * 0.42, cy - r * 0.42, r * 0.84, r * 0.84, r * 0.1)
    ctx.stroke()
  },
  counter: (ctx, cx, cy, r) => {
    // A bench from the front: a thick top slab, two legs, one stretcher. The
    // slab is the silhouette — a counter is a surface you put things on.
    roundRect(ctx, cx - r * 0.9, cy - r * 0.56, r * 1.8, r * 0.36, r * 0.1)
    ctx.stroke()
    line(ctx, cx - r * 0.62, cy - r * 0.2, cx - r * 0.62, cy + r * 0.72)
    line(ctx, cx + r * 0.62, cy - r * 0.2, cx + r * 0.62, cy + r * 0.72)
    line(ctx, cx - r * 0.62, cy + r * 0.38, cx + r * 0.62, cy + r * 0.38)
  },
  board: (ctx, cx, cy, r, lw) => {
    // The board with a knife lying on it. The knife is a filled wedge rather
    // than a stroked diagonal: a line corner to corner turns the whole badge
    // into a crossed-out box, and the blade is what says "chop" — so it gets
    // a tip, a spine and a handle, and it stops short of both corners.
    roundRect(ctx, cx - r * 0.86, cy - r * 0.7, r * 1.72, r * 1.4, r * 0.16)
    ctx.stroke()
    ctx.beginPath()
    ctx.moveTo(cx - r * 0.3, cy + r * 0.06)
    ctx.lineTo(cx + r * 0.54, cy - r * 0.46)
    ctx.quadraticCurveTo(cx + r * 0.16, cy + r * 0.06, cx - r * 0.16, cy + r * 0.42)
    ctx.closePath()
    ctx.fill()
    ctx.save()
    ctx.lineWidth = lw * 1.4
    line(ctx, cx - r * 0.25, cy + r * 0.26, cx - r * 0.66, cy + r * 0.56)
    ctx.restore()
  },
  stove: (ctx, cx, cy, r) => {
    // A hob from above: burner ring, inner ring, flame in the middle of it.
    ctx.beginPath()
    ctx.arc(cx, cy, r * 0.86, 0, TAU)
    ctx.stroke()
    ctx.beginPath()
    ctx.arc(cx, cy, r * 0.5, 0, TAU)
    ctx.stroke()
    badgeFlame(ctx, cx, cy + r * 0.34, r * 0.42)
  },
  oven: (ctx, cx, cy, r) => {
    // A front-loading box: handle across the top, window below it, flame inside.
    // Nothing like the hob in outline, which is the point of drawing it at all.
    roundRect(ctx, cx - r * 0.86, cy - r * 0.8, r * 1.72, r * 1.6, r * 0.24)
    ctx.stroke()
    line(ctx, cx - r * 0.66, cy - r * 0.34, cx + r * 0.66, cy - r * 0.34)
    roundRect(ctx, cx - r * 0.46, cy - r * 0.1, r * 0.92, r * 0.66, r * 0.12)
    ctx.stroke()
    badgeFlame(ctx, cx, cy + r * 0.5, r * 0.3)
  },
  crate: (ctx, cx, cy, r, lw) => {
    // A slatted box: the slats run through, the corner posts stand proud. Its
    // ingredient is not drawn — a crate on a route strip means "from a crate",
    // and the ingredient has its own icon one hop earlier.
    roundRect(ctx, cx - r * 0.84, cy - r * 0.62, r * 1.68, r * 1.32, r * 0.1)
    ctx.stroke()
    ctx.save()
    ctx.lineWidth = lw * 0.72
    line(ctx, cx - r * 0.84, cy - r * 0.16, cx + r * 0.84, cy - r * 0.16)
    line(ctx, cx - r * 0.84, cy + r * 0.26, cx + r * 0.84, cy + r * 0.26)
    ctx.restore()
    line(ctx, cx - r * 0.5, cy - r * 0.62, cx - r * 0.5, cy + r * 0.7)
    line(ctx, cx + r * 0.5, cy - r * 0.62, cx + r * 0.5, cy + r * 0.7)
  },
  plate_stack: (ctx, cx, cy, r) => {
    // Clean plates on their shelf. Three of them, because a stack of one is a
    // plate and a stack of two is a coincidence.
    for (let i = 0; i < 3; i += 1) {
      const y = cy + r * 0.4 - i * r * 0.3
      ctx.beginPath()
      ctx.ellipse(cx, y, r * 0.62, r * 0.17, 0, 0, TAU)
      ctx.stroke()
    }
    line(ctx, cx - r * 0.9, cy + r * 0.76, cx + r * 0.9, cy + r * 0.76)
  },
  sink: (ctx, cx, cy, r) => {
    // The basin and the spout arching over its back rim.
    roundRect(ctx, cx - r * 0.86, cy - r * 0.2, r * 1.72, r * 0.94, r * 0.2)
    ctx.stroke()
    ctx.beginPath()
    ctx.moveTo(cx + r * 0.5, cy - r * 0.2)
    ctx.lineTo(cx + r * 0.5, cy - r * 0.7)
    ctx.lineTo(cx - r * 0.1, cy - r * 0.7)
    ctx.stroke()
  },
  bin: (ctx, cx, cy, r, lw) => {
    // A tapered can with a lid and a knob. The taper is the whole tell: it is
    // the only station in the kitchen that is narrower at the bottom.
    ctx.beginPath()
    ctx.moveTo(cx - r * 0.62, cy - r * 0.4)
    ctx.lineTo(cx + r * 0.62, cy - r * 0.4)
    ctx.lineTo(cx + r * 0.46, cy + r * 0.8)
    ctx.lineTo(cx - r * 0.46, cy + r * 0.8)
    ctx.closePath()
    ctx.stroke()
    roundRect(ctx, cx - r * 0.82, cy - r * 0.66, r * 1.64, r * 0.22, r * 0.1)
    ctx.stroke()
    line(ctx, cx, cy - r * 0.66, cx, cy - r * 0.86)
    ctx.save()
    ctx.lineWidth = lw * 0.7
    line(ctx, cx - r * 0.22, cy - r * 0.16, cx - r * 0.18, cy + r * 0.56)
    line(ctx, cx + r * 0.22, cy - r * 0.16, cx + r * 0.18, cy + r * 0.56)
    ctx.restore()
  },
  hatch: (ctx, cx, cy, r, lw) => {
    // The pass: an opening, a sill, and a plate going out through it.
    //
    // This pair used to be one drawing mirrored — the same conveyor with the
    // slats and the arrow reversed — on the theory that the two ends of the
    // plate loop should read as one loop and only the direction had to be
    // unambiguous. A playtester read them as the same station and served into
    // the wrong one, so the theory is dead: they are two objects now. The pass
    // is a hole in the wall you push a plate through; the return is a chute
    // things come back down. Nothing about either says "conveyor", so there is
    // no shared silhouette left to confuse.
    //
    // Open at the bottom rather than a closed box, which is also what keeps it
    // clear of the oven — the confusion the conveyor was drawn to escape.
    ctx.beginPath()
    ctx.moveTo(cx - r * 0.66, cy + r * 0.26)
    ctx.lineTo(cx - r * 0.66, cy - r * 0.56)
    ctx.lineTo(cx + r * 0.66, cy - r * 0.56)
    ctx.lineTo(cx + r * 0.66, cy + r * 0.26)
    ctx.stroke()

    // The sill, running wider than the frame: the ledge a plate is pushed
    // across, and the mark that says "window" rather than "doorway".
    line(ctx, cx - r * 0.94, cy + r * 0.3, cx + r * 0.94, cy + r * 0.3)

    // The plate in the opening, mid-push. Thinner than the frame so the
    // silhouette stays the window and the plate stays cargo.
    ctx.save()
    ctx.lineWidth = lw * 0.8
    ctx.beginPath()
    ctx.ellipse(cx, cy + r * 0.02, r * 0.42, r * 0.15, 0, 0, TAU)
    ctx.stroke()
    ctx.restore()

    // Which way it goes. Under the sill, where it cannot be read as part of
    // the window.
    arrow(ctx, cx, cy + r * 0.66, r * 0.8, 1)
  },
  plate_return: (ctx, cx, cy, r, lw) => {
    // The washing-up chute: a slide dropping in from the top right into a
    // mouth, and the dirty plates piling under it.
    //
    // A slope is the one gesture no other badge in this kitchen makes, so it
    // survives a 26px ticket and peripheral vision — which is more than the
    // mirrored arrow it replaces ever did. Two plates, because the pile is
    // what says these are dirty ones waiting rather than clean ones to take:
    // `plate_stack` is three plates squared up on a shelf, this is a heap
    // under a chute.
    // The chute lives in the top half and the pile in the bottom half, clear
    // of each other: overlapped, the two merge into one blob at 26px and the
    // whole badge reads as the chopping board's knife.
    ctx.beginPath()
    ctx.moveTo(cx + r * 0.94, cy - r * 0.76)
    ctx.lineTo(cx - r * 0.3, cy - r * 0.14)
    ctx.lineTo(cx - r * 0.3, cy + r * 0.2)
    ctx.lineTo(cx + r * 0.94, cy - r * 0.42)
    ctx.closePath()
    ctx.stroke()

    // The lip at the low end, so the chute ends in a mouth rather than in a
    // cut-off band.
    ctx.save()
    ctx.lineWidth = lw * 0.7
    line(ctx, cx - r * 0.44, cy - r * 0.08, cx - r * 0.44, cy + r * 0.26)
    ctx.restore()

    for (let i = 0; i < 2; i += 1) {
      ctx.beginPath()
      ctx.ellipse(cx - r * 0.16, cy + r * 0.8 - i * r * 0.3, r * 0.56, r * 0.18, 0, 0, TAU)
      ctx.stroke()
    }
  },
  extinguisher_rack: (ctx, cx, cy, r, lw) => {
    // The extinguisher itself, as big as the badge will hold.
    //
    // The tile is a red wall panel with a bottle hanging in it, and the first
    // pass drew the panel too — which shrank the bottle to a squiggle. The
    // bottle is the whole point: this is the badge somebody reads in a hurry
    // with a pan on fire. Nothing else in the kitchen is bottle-shaped, so
    // dropping the panel costs no distinctness and buys every pixel back.
    roundRect(ctx, cx - r * 0.4, cy - r * 0.34, r * 0.8, r * 1.2, r * 0.3)
    ctx.stroke()
    roundRect(ctx, cx - r * 0.17, cy - r * 0.64, r * 0.34, r * 0.32, r * 0.09)
    ctx.stroke()
    line(ctx, cx - r * 0.14, cy - r * 0.62, cx + r * 0.42, cy - r * 0.8)
    // The hose, over the shoulder and down the side.
    ctx.save()
    ctx.lineWidth = lw * 0.85
    ctx.beginPath()
    ctx.moveTo(cx + r * 0.17, cy - r * 0.46)
    ctx.quadraticCurveTo(cx + r * 0.74, cy - r * 0.2, cx + r * 0.62, cy + r * 0.44)
    ctx.stroke()
    ctx.restore()
  },
}

/**
 * What a station type nobody has drawn yet gets.
 *
 * Unreachable for any type in the contract — `BADGE_PATHS` is exhaustive by its
 * own `Record` — and kept only for the runtime edge where a server sends a
 * station type a stale client has never heard of. A square is the honest answer
 * to "something is here and I do not know what": it is deliberately not a
 * silhouette anybody could mistake for a real one.
 */
export const badgeFallback: BadgePath = (ctx, cx, cy, r) => {
  roundRect(ctx, cx - r * 0.8, cy - r * 0.8, r * 1.6, r * 1.6, r * 0.2)
  ctx.stroke()
}
